import { useEffect, useMemo, useState } from 'react';
import { EvaluationScoreLabel, EvaluationStatus, PerformEvaluationScore } from '../../../commonTypes/evaluation/calculate.types';
import { ScoreSelection } from '.././ScoreSelection';
import { MixerLoader, Modal, YesNo } from '../../Common';
import { useNavigate, useParams } from 'react-router';
import useHttpPerformEvaluation from '../../../hooks/user/userApi/useHttpPerformEvaluation';
import { useHttpGetEvaluationOnMount } from '../../../hooks';
import { displayRoles } from '../../../util/evaluation/mappers';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import StartEvaluationProcess from '../StartEvaluationProcess';
import { EvaluationReview } from '../EvaluationReview';
import { useTranslation } from '../../../hooks/useTranslation';

export default function EvaluationPerformWizard() {
    const { t } = useTranslation('evaluations');
    const { id: evaluationId } = useParams();
    const navigate = useNavigate();
    const [step, setStep] = useState<number | 'review'>(0);
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const auth = useSelector((state: RootState) => state.auth);

    const { exec: performEvaluation } = useHttpPerformEvaluation();
    const { data, isReady } = useHttpGetEvaluationOnMount(evaluationId as string);
    const sortedTopicScores = useMemo(() => {
        if (!data?.topicScores) return [];

        return [...data.topicScores].sort((a, b) => a.topic.name.localeCompare(b.topic.name));
    }, [data?.topicScores]);

    const partialEvaluatorScores: PerformEvaluationScore[] = useMemo(() => {
        return sortedTopicScores.map(({ topic, score, note }) => ({
            topic,
            score,
            note,
        }));
    }, [sortedTopicScores]);

    const [responses, setResponses] = useState<PerformEvaluationScore[]>([]);

    useEffect(() => {
        if (data) {
            if (
                (data.evaluatorUserSub !== auth.value.userSub && !data.isAnonymous) ||
                data.status === EvaluationStatus.UPDATE_REQUESTED ||
                data.status === EvaluationStatus.SUBMITTED
            ) {
                window.location.href = '/404';
            }
        }
    }, [data]);

    useEffect(() => {
        if (!data && isReady) window.location.href = '/404';
    }, [data, isReady]);

    useEffect(() => {
        if (partialEvaluatorScores.length > 0 && responses.length === 0) {
            setResponses(partialEvaluatorScores);
        }
    }, [partialEvaluatorScores]);

    const handleScoreChange = (newScore: EvaluationScoreLabel) => {
        if (typeof step === 'number' && step > 0 && step <= responses.length) {
            setResponses(prev => {
                const updated = [...prev];
                const current = updated[step - 1];

                updated[step - 1] = {
                    ...current,
                    score: current.score === newScore ? null : newScore,
                };

                return updated;
            });
        }
    };

    const handleNoteChange = (note: string) => {
        if (typeof step === 'number') {
            setResponses(prev => {
                const updated = [...prev];
                const index = step - 1;

                if (!updated[index]) return prev;

                updated[index] = {
                    ...updated[index],
                    note,
                };

                return updated;
            });
        }
    };

    const onSubmit = async () => {
        if (!evaluationId) return alert('Evaluation ID is missing');

        const topicScores = responses.map(response => ({
            topicId: response.topic.id,
            score: response.score ? response.score.toString() : null,
            note: response.note,
        }));

        await performEvaluation(evaluationId, { topicScores });
        navigate(`/evaluation-preview/${evaluationId}`);
    };

    const handleSubmit = async () => {
        const isEmptyResponse = responses.every(response => response.score === null);

        if (isEmptyResponse) {
            setIsConfirmationModalOpen(true);
            return;
        }

        await onSubmit();
    };

    if (step === 0) {
        return isReady ? (
            <StartEvaluationProcess
                onStart={() => setStep(1)}
                onCancel={() => navigate('/evaluation')}
                isAnonymous={data?.isAnonymous}
                evaluatorRole={data?.evaluatorToEvaluateeRole && displayRoles(data?.evaluatorToEvaluateeRole.direction)}
                evaluateeName={data?.evaluatee?.profile ? `${data?.evaluatee?.profile.givenName} ${data?.evaluatee?.profile.familyName}` : undefined}
                status={data?.status as EvaluationStatus.REQUESTED | EvaluationStatus.UPDATE_APPROVED}
            />
        ) : (
            <MixerLoader position="centerOnScreen" />
        );
    }

    const handleCancel = () => {
        setResponses(partialEvaluatorScores);
        setStep(0);
        navigate('/evaluation');
    };

    if (step === 'review') {
        return (
            <>
                <EvaluationReview
                    topicScores={sortedTopicScores}
                    responses={responses}
                    onEdit={index => {
                        setStep(index + 1);
                        setIsEditing(true);
                    }}
                    onSubmit={handleSubmit}
                    onCancel={handleCancel}
                />
                <Modal isVisible={isConfirmationModalOpen} closeModal={() => setIsConfirmationModalOpen(false)}>
                    <YesNo
                        title={t('confirmationModalTitle')}
                        description={t('confirmationModalDescription')}
                        onYes={() => {
                            void onSubmit();
                        }}
                        onNo={() => setIsConfirmationModalOpen(false)}
                    />
                </Modal>
            </>
        );
    }

    const current = sortedTopicScores[step - 1];

    const scoreGuide: Record<EvaluationScoreLabel, { description: string }> = {
        [EvaluationScoreLabel.NEGATIVE]: {
            description: current?.topic.negativeScoreDescription || '',
        },
        [EvaluationScoreLabel.NEUTRAL]: {
            description: current?.topic.neutralScoreDescription || '',
        },
        [EvaluationScoreLabel.POSITIVE]: {
            description: current?.topic.positiveScoreDescription || '',
        },
    };

    const handleNext = async () => {
        if (isEditing) {
            setStep('review');
            setIsEditing(false);
        } else if (step < sortedTopicScores.length!) {
            setStep(step + 1);
        } else {
            setStep('review');
        }
    };

    const handleBack = () => {
        if (step === 1) {
            setStep(0);
        } else if (step > 1) {
            setStep(step - 1);
        }
    };

    const handleStepClick = (stepNumber: number) => {
        setStep(stepNumber);
    };

    const completedSteps = responses
        .map((response, index) => (response?.score !== null && response?.score !== undefined ? index + 1 : null))
        .filter((stepNumber): stepNumber is number => stepNumber !== null);

    return (
        <ScoreSelection
            topicId={current?.topic.id || ''}
            evaluationId={evaluationId || ''}
            topicName={current?.topic.name || ''}
            topicDescription={current?.topic.description || ''}
            currentStep={step}
            totalSteps={sortedTopicScores.length}
            selectedScore={responses[step - 1]}
            onSelect={handleScoreChange}
            scoreGuide={scoreGuide}
            note={responses[step - 1]?.note || ''}
            onNoteChange={handleNoteChange}
            onBack={handleBack}
            onNext={handleNext}
            onStepClick={handleStepClick}
            completedSteps={completedSteps}
            isNoteRequired={data?.isNoteRequired}
        />
    );
}
